import { useI18n } from '@ecom/i18n';
import { Button, Field, Input } from '@ecom/ui-kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactElement, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useLoginMutation } from '../api';
import { AuthLayout } from '../components/AuthLayout';
import { loginFormSchema, type LoginFormValues } from '../schemas';

import { ApiError } from '@/lib/api-client';
import { whenDefined } from '@/lib/optional-props';
import { useSession } from '@/lib/session';

/**
 * Same shape as apps/merchant-dashboard's own LoginPage - same endpoint, same schema, same uniform-error discipline: the API gives ONE
 * answer for every rejection cause, and this page shows ONE sentence, in the person's language, that names none of them (which would
 * reopen the enumeration channel the API closes).
 *
 * There is no second step: a correct password opens the session, and the first request tells the server what that account may do.
 */
export function LoginPage(): ReactElement {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { setSession } = useSession();
  const loginMutation = useLoginMutation();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({ resolver: zodResolver(loginFormSchema) });

  async function onSubmit(values: LoginFormValues): Promise<void> {
    setFormError(null);
    try {
      const result = await loginMutation.mutateAsync(values);
      setSession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        mustChangePassword: result.mustChangePassword,
      });
      // A provisional password may only be used to choose a real one. Otherwise the home decides (the superadmin's dashboard, the
      // administrator's merchants): see `Home` in App.tsx.
      void navigate(result.mustChangePassword ? '/change-password' : '/', {
        replace: true,
      });
    } catch (error) {
      // One sentence for every refusal; only « no answer at all » is told apart (it says nothing about any account).
      setFormError(error instanceof ApiError ? t('auth.login.invalid') : t('errors.network'));
    }
  }

  return (
    <AuthLayout title={t('admin.auth.loginTitle')} subtitle={t('admin.auth.loginSubtitle')}>
      <form
        onSubmit={(event) => {
          void handleSubmit(onSubmit)(event);
        }}
        noValidate
        className="flex flex-col gap-4"
      >
        <Field
          label={t('merchant.staff.form.emailLabel')}
          {...whenDefined('error', errors.email?.message)}
        >
          {(bind) => (
            <Input {...bind} {...register('email')} type="email" autoComplete="email" autoFocus />
          )}
        </Field>

        <Field
          label={t('admin.auth.passwordLabel')}
          {...whenDefined('error', errors.password?.message)}
        >
          {(bind) => (
            <Input
              {...bind}
              {...register('password')}
              type="password"
              autoComplete="current-password"
            />
          )}
        </Field>

        {formError !== null ? (
          <p role="alert" className="rounded-md bg-criticalSoft px-3 py-2 text-sm text-critical">
            {formError}
          </p>
        ) : null}

        <Button type="submit" loading={loginMutation.isPending} className="mt-1 w-full">
          {t('admin.auth.loginButton')}
        </Button>
      </form>
    </AuthLayout>
  );
}
